/**
 * Markers for bodies arriving at an eclipse's own degree.
 *
 * <p>Pure functions over the study response and the chart's own dates. The backend decides WHICH
 * arrivals there are; this decides what they look like and what the floater says about them.
 */

/** The overlay id, so a re-run replaces the previous markers rather than stacking on them. */
export const ECLIPSE_TRANSIT_OVERLAY_ID = 'eclipse-degree-transits';

/**
 * Solar and lunar are told apart by SHAPE as well as colour.
 *
 * <p>Colour alone carries the distinction only for readers who can see the difference between amber
 * and blue, and it is the one thing the study asks to be able to tell at a glance. A circle and a
 * diamond survive a monochrome screenshot and a colour-blind reader; the colours are then a second,
 * faster channel for everyone else rather than the only one.
 */
export const SOLAR_STYLE = { color: '#f57f17', symbol: 'circle', label: 'Solar' };
export const LUNAR_STYLE = { color: '#3949ab', symbol: 'diamond', label: 'Lunar' };

/** How many bodies a bar's canvas label names before it counts the rest. */
export const MAX_LABEL_BODIES = 2;

/** Two letters, matching the abbreviations the wheel and the other overlays already use. */
const ABBREV = {
  Sun: 'Su', Moon: 'Mo', Mercury: 'Me', Venus: 'Ve', Mars: 'Ma', Jupiter: 'Ju',
  Saturn: 'Sa', Uranus: 'Ur', Neptune: 'Ne', Pluto: 'Pl', Rahu: 'Ra', Ketu: 'Ke',
};
export function abbrev(name) {
  if (!name) return '?';
  return ABBREV[name] || String(name).slice(0, 2);
}

/**
 * The chart date on or before a calendar date, or -1.
 *
 * <p>Backwards only, unlike the CTS snapping which takes the nearest in either direction. A transit
 * is an event with a direction in time: a Saturday arrival is known to the market on the Monday, and
 * placing it on the preceding Friday would put a marker on a bar that closed before the thing
 * happened. The CTS marks a confluence, which is a description of a day rather than news arriving.
 */
export function barOnOrAfter(dates, target, maxGapDays = 5) {
  if (!Array.isArray(dates) || dates.length === 0 || !target) return -1;
  const t = Date.parse(target);
  if (Number.isNaN(t)) return -1;

  let lo = 0;
  let hi = dates.length - 1;
  if (Date.parse(dates[hi]) < t) return -1;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (Date.parse(dates[mid]) < t) lo = mid + 1;
    else hi = mid;
  }
  const gap = (Date.parse(dates[lo]) - t) / 86400000;
  // A gap larger than a long weekend means the chart simply has no bar for it — a transit during a
  // trading halt must not be drawn on whatever bar happens to come next.
  return gap <= maxGapDays ? lo : -1;
}

/** One transit written out for the floater, per the study's four required fields. */
export function transitLines(hit) {
  if (!hit) return [];
  return [
    `${hit.eclipseType} Eclipse on ${hit.eclipseDate} at ${hit.eclipseDegree}`,
    `  ${hit.body} at ${hit.degree} — ${hit.motion}`,
    `  ${hit.kind === 'station' ? 'stationed at' : 'crossed'} the degree on ${hit.date}`,
  ];
}

/**
 * One marker per bar, carrying every arrival that landed on it.
 *
 * <p>Merged rather than stacked. Several bodies can reach one degree within a day of each other, and
 * three markers on one bar read as three findings about that bar rather than one bar with three
 * bodies on it — and at this symbol size they would overlap into an unreadable blob anyway.
 *
 * <p>A bar carrying both a solar and a lunar arrival takes the SOLAR style and says so in the
 * floater. Splitting it into two markers would put them on the same pixel; picking silently would
 * make the shape a lie half the time.
 */
export function buildEclipseTransitPoints(transits, barData) {
  const dates = barData && Array.isArray(barData.dates) ? barData.dates : [];
  const ohlc = barData && Array.isArray(barData.ohlc) ? barData.ohlc : [];
  const list = Array.isArray(transits) ? transits : [];

  const byIndex = new Map();
  let offChart = 0;

  list.forEach((hit) => {
    if (!hit || !hit.date) return;
    const idx = barOnOrAfter(dates, hit.date);
    if (idx < 0) { offChart += 1; return; }
    if (!byIndex.has(idx)) byIndex.set(idx, []);
    byIndex.get(idx).push(hit);
  });

  const points = [...byIndex.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([idx, hits]) => {
      const bar = ohlc[idx] || [];
      const high = bar[3];
      if (typeof high !== 'number' || !Number.isFinite(high)) return null;

      const solar = hits.some(h => h.eclipseType === 'Solar');
      const style = solar ? SOLAR_STYLE : LUNAR_STYLE;
      const bodies = [];
      hits.forEach((h) => { if (!bodies.includes(h.body)) bodies.push(h.body); });

      const shown = bodies.slice(0, MAX_LABEL_BODIES).map(abbrev).join(' ');
      const rest = bodies.length - Math.min(bodies.length, MAX_LABEL_BODIES);

      return {
        date: dates[idx],
        price: high,
        position: 'above',
        color: style.color,
        symbol: style.symbol,
        label: rest > 0 ? `${shown}+${rest}` : shown,
        title: dates[idx],
        tooltipLines: hits.flatMap((h, i) => (i > 0 ? ['', ...transitLines(h)] : transitLines(h))),
        // Kept so a caller can count what it drew without re-deriving it from the tooltip.
        solar,
        bodies,
      };
    })
    .filter(Boolean);

  return {
    points,
    offChart,
    solar: points.filter(p => p.solar).length,
    lunar: points.filter(p => !p.solar).length,
  };
}

/** A line per eclipse for the journal, so a run can be compared against an earlier one. */
export function eclipseTransitJournalLines(transits) {
  const list = Array.isArray(transits) ? transits : [];
  if (list.length === 0) return ['No planetary transits over an eclipse degree in this range.'];

  const byEclipse = new Map();
  list.forEach((h) => {
    const key = `${h.eclipseType} eclipse ${h.eclipseDate} at ${h.eclipseDegree}`;
    if (!byEclipse.has(key)) byEclipse.set(key, []);
    byEclipse.get(key).push(h);
  });

  const out = [`${list.length} transit${list.length === 1 ? '' : 's'} over `
    + `${byEclipse.size} eclipse degree${byEclipse.size === 1 ? '' : 's'}`];
  byEclipse.forEach((hits, key) => {
    out.push('');
    out.push(key);
    hits.forEach((h) => {
      out.push(`    ${h.date}  ${String(h.body).padEnd(8)}${String(h.motion).padEnd(11)}${h.degree}`
        + `${h.kind === 'station' ? '  (stationed)' : ''}`);
    });
  });
  return out;
}
